import React, { useState } from 'react';
import { 
  Cog6ToothIcon,
  PaintBrushIcon,
  ShieldCheckIcon,
  InformationCircleIcon,
  SparklesIcon,
  UserGroupIcon
} from '@heroicons/react/24/outline';
import { FormTemplate } from '../../types/formBuilder';
import { useLanguage } from '../../contexts/LanguageContext';
import { useCustomers } from '../../hooks/useCustomers';
import Modal from '../common/Modal';
import { Button } from '../common/Button';

interface FormSettingsProps {
  isOpen: boolean;
  onClose: () => void;
  template: FormTemplate;
  onUpdateTemplate: (updates: Partial<FormTemplate>) => void;
}

const FormSettings: React.FC<FormSettingsProps> = ({
  isOpen,
  onClose,
  template,
  onUpdateTemplate
}) => {
  const { t } = useLanguage();
  const [activeTab, setActiveTab] = useState<'general' | 'appearance' | 'permissions'>('general');

  const handleUpdate = (updates: Partial<FormTemplate>) => {
    onUpdateTemplate(updates);
  };

  const handleSettingsUpdate = (updates: Partial<FormTemplate['settings']>) => {
    handleUpdate({
      settings: {
        ...template.settings,
        ...updates
      }
    });
  };

  const handlePermissionsUpdate = (updates: Partial<FormTemplate['permissions']>) => {
    handleUpdate({
      permissions: {
        ...template.permissions,
        ...updates
      }
    });
  };

  const tabs = [
    { key: 'general', label: t('formBuilder.formSettings.generalTab') || 'General' },
    { key: 'appearance', label: t('formBuilder.formSettings.appearanceTab') || 'Appearance' },
    { key: 'permissions', label: t('formBuilder.formSettings.permissionsTab') || 'Permissions' }
  ];

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t('formBuilder.formSettings.title') || 'Form Settings'}
      size="4xl"
    >
      <div className="bg-gradient-to-br from-indigo-50 via-white to-purple-50 dark:from-gray-800 dark:via-gray-900 dark:to-gray-800 min-h-[600px]">
        {/* Header */}
        <div className="p-6 bg-gradient-to-r from-indigo-500 to-indigo-600 text-white">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-white/20 backdrop-blur-sm rounded-lg flex items-center justify-center">
              <Cog6ToothIcon className="h-6 w-6 text-white" />
            </div>
            <div>
              <h3 className="text-xl font-semibold">
                {t('formBuilder.formSettings.title') || 'Form Settings'}
              </h3>
              <p className="text-indigo-100 text-sm">
                {t('formBuilder.formSettings.subtitle') || 'Configure your form behavior and appearance'}
              </p>
            </div>
          </div>
        </div>

        {/* Content - Single Page Layout */}
        <div className="p-6 space-y-6 overflow-y-auto max-h-[500px]">
          
          {/* General Settings Section */}
          <div className="bg-white/60 backdrop-blur-sm dark:bg-gray-800/60 rounded-xl p-6 shadow-lg border border-white/20">
            <div className="flex items-center space-x-3 mb-4">
              <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-blue-600 rounded-lg flex items-center justify-center">
                <InformationCircleIcon className="h-5 w-5 text-white" />
              </div>
              <h4 className="text-lg font-semibold text-gray-900 dark:text-white">
                {t('formBuilder.formSettings.general') || 'General Settings'}
              </h4>
            </div>
            <GeneralSettings
              template={template}
              onUpdate={handleUpdate}
              onSettingsUpdate={handleSettingsUpdate}
            />
          </div>

          {/* Appearance Settings Section */}
          <div className="bg-white/60 backdrop-blur-sm dark:bg-gray-800/60 rounded-xl p-6 shadow-lg border border-white/20">
            <div className="flex items-center space-x-3 mb-4">
              <div className="w-8 h-8 bg-gradient-to-r from-purple-500 to-purple-600 rounded-lg flex items-center justify-center">
                <PaintBrushIcon className="h-5 w-5 text-white" />
              </div>
              <h4 className="text-lg font-semibold text-gray-900 dark:text-white">
                {t('formBuilder.formSettings.appearance') || 'Appearance Settings'}
              </h4>
            </div>
            <AppearanceSettings
              template={template}
              onSettingsUpdate={handleSettingsUpdate}
            />
          </div>

          {/* Permissions Settings Section */}
          <div className="bg-white/60 backdrop-blur-sm dark:bg-gray-800/60 rounded-xl p-6 shadow-lg border border-white/20">
            <div className="flex items-center space-x-3 mb-4">
              <div className="w-8 h-8 bg-gradient-to-r from-green-500 to-green-600 rounded-lg flex items-center justify-center">
                <ShieldCheckIcon className="h-5 w-5 text-white" />
              </div>
              <h4 className="text-lg font-semibold text-gray-900 dark:text-white">
                {t('formBuilder.formSettings.permissions') || 'Permissions Settings'}
              </h4>
            </div>
            <PermissionsSettings
              template={template}
              onPermissionsUpdate={handlePermissionsUpdate}
            />
          </div>

          {/* Customer Portal Settings Section */}
          <div className="bg-white/60 backdrop-blur-sm dark:bg-gray-800/60 rounded-xl p-6 shadow-lg border border-white/20">
            <div className="flex items-center space-x-3 mb-4">
              <div className="w-8 h-8 bg-gradient-to-r from-indigo-500 to-indigo-600 rounded-lg flex items-center justify-center">
                <UserGroupIcon className="h-5 w-5 text-white" />
              </div>
              <h4 className="text-lg font-semibold text-gray-900 dark:text-white">
                Ügyfélportál beállítások
              </h4>
            </div>
            <CustomerPortalSettings
              template={template}
              onUpdate={handleUpdate}
            />
          </div>

        </div>

        {/* Footer */}
        <div className="flex justify-end space-x-3 p-6 border-t border-gray-200 dark:border-gray-600 bg-white/50 dark:bg-gray-800/50">
          <Button variant="outline" onClick={onClose}>
            {t('common.close') || 'Close'}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

// General Settings Tab
const GeneralSettings: React.FC<{
  template: FormTemplate;
  onUpdate: (updates: Partial<FormTemplate>) => void;
  onSettingsUpdate: (updates: Partial<FormTemplate['settings']>) => void;
}> = ({ template, onUpdate, onSettingsUpdate }) => {
  const { t } = useLanguage();

  return (
    <div className="space-y-4">
      {/* Form Name */}
      <div>
        <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">
          {t('formBuilder.properties.name') || 'Form Name'}
        </label>
        <input
          type="text"
          value={template.name}
          onChange={(e) => onUpdate({ name: e.target.value })}
          maxLength={50}
          className="form-input w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-1 focus:ring-primary-500 focus:border-primary-500 transition-colors"
        />
        <div className="text-xs text-gray-500 dark:text-gray-400 mt-1 text-right">
          {template.name?.length || 0}/50
        </div>
      </div>

      {/* Description */}
      <div>
        <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">
          {t('formBuilder.properties.description') || 'Description'}
        </label>
        <textarea
          value={template.description || ''}
          onChange={(e) => onUpdate({ description: e.target.value })}
          rows={2}
          maxLength={200}
          className="form-input w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-1 focus:ring-primary-500 focus:border-primary-500 transition-colors resize-none"
        />
        <div className="text-xs text-gray-500 dark:text-gray-400 mt-1 text-right">
          {template.description?.length || 0}/200
        </div>
      </div>

      {/* Category */}
      <div>
        <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">
          {t('formBuilder.properties.category') || 'Category'}
        </label>
        <input
          type="text"
          value={template.category || ''}
          onChange={(e) => onUpdate({ category: e.target.value })}
          placeholder={t('formBuilder.properties.categoryPlaceholder') || 'e.g., Customer Forms, Internal Forms'}
          className="form-input w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-1 focus:ring-primary-500 focus:border-primary-500 transition-colors"
        />
      </div>

      {/* Tags */}
      <div>
        <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">
          {t('formBuilder.properties.tags') || 'Tags'}
        </label>
        <input
          type="text"
          value={template.tags?.join(', ') || ''}
          onChange={(e) => onUpdate({ tags: e.target.value.split(',').map(tag => tag.trim()).filter(Boolean) })}
          placeholder={t('formBuilder.properties.tagsPlaceholder') || 'tag1, tag2, tag3'}
          className="form-input w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-1 focus:ring-primary-500 focus:border-primary-500 transition-colors"
        />
      </div>

      {/* Form Behavior Checkboxes */}
      <div className="space-y-3 pt-4 border-t border-gray-200 dark:border-gray-600">
        <h5 className="text-sm font-semibold text-gray-900 dark:text-white">
          {t('formBuilder.formSettings.formBehavior') || 'Form Behavior'}
        </h5>
        
        {/* Allow Drafts */}
        <div className="flex items-center space-x-3 p-3 bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-700 dark:to-gray-600 rounded-lg">
          <input
            type="checkbox"
            id="allowDrafts"
            checked={template.settings.allowDrafts || false}
            onChange={(e) => onSettingsUpdate({ allowDrafts: e.target.checked })}
            className="w-4 h-4 text-primary-600 bg-white border-gray-300 rounded focus:ring-primary-500 focus:ring-1"
          />
          <label htmlFor="allowDrafts" className="text-xs font-medium text-gray-900 dark:text-gray-100">
            {t('formBuilder.formSettings.allowDrafts') || 'Allow saving drafts'}
          </label>
        </div>

        {/* Multi-step */}
        <div className="flex items-center space-x-3 p-3 bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-700 dark:to-gray-600 rounded-lg">
          <input
            type="checkbox"
            id="multiStep"
            checked={template.settings.multiStep || false}
            onChange={(e) => onSettingsUpdate({ multiStep: e.target.checked })}
            className="w-4 h-4 text-primary-600 bg-white border-gray-300 rounded focus:ring-primary-500 focus:ring-1"
          />
          <label htmlFor="multiStep" className="text-xs font-medium text-gray-900 dark:text-gray-100">
            {t('formBuilder.formSettings.multiStep') || 'Multi-step form'}
          </label>
        </div>

        {/* Show Progress Bar */}
        <div className="flex items-center space-x-3 p-3 bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-700 dark:to-gray-600 rounded-lg">
          <input
            type="checkbox"
            id="showProgressBar"
            checked={template.settings.showProgressBar || false}
            onChange={(e) => onSettingsUpdate({ showProgressBar: e.target.checked })}
            className="w-4 h-4 text-primary-600 bg-white border-gray-300 rounded focus:ring-primary-500 focus:ring-1"
          />
          <label htmlFor="showProgressBar" className="text-xs font-medium text-gray-900 dark:text-gray-100">
            {t('formBuilder.formSettings.showProgressBar') || 'Show progress bar'}
          </label>
        </div>
      </div>
    </div>
  );
};

// Appearance Settings Tab
const AppearanceSettings: React.FC<{
  template: FormTemplate;
  onSettingsUpdate: (updates: Partial<FormTemplate['settings']>) => void;
}> = ({ template, onSettingsUpdate }) => {
  const { t } = useLanguage();

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">
          {t('formBuilder.formSettings.displaySettings') || 'Display Settings'}
        </h3>
        
        <div className="space-y-4">
          {/* Form Title */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t('formBuilder.formSettings.formTitle') || 'Form Title'}
            </label>
            <input
              type="text"
              value={template.settings.title || ''}
              onChange={(e) => onSettingsUpdate({ title: e.target.value })}
              placeholder="Leave empty to use form name"
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            />
          </div>

          {/* Form Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t('formBuilder.formSettings.formDescription') || 'Form Description'}
            </label>
            <textarea
              value={template.settings.description || ''}
              onChange={(e) => onSettingsUpdate({ description: e.target.value })}
              rows={3}
              placeholder="Description shown to users"
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            />
          </div>

          {/* Submit Button Text */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t('formBuilder.formSettings.submitButtonText') || 'Submit Button Text'}
            </label>
            <input
              type="text"
              value={template.settings.submitButtonText || ''}
              onChange={(e) => onSettingsUpdate({ submitButtonText: e.target.value })}
              placeholder="Submit"
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            />
          </div>

          {/* Cancel Button Text */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t('formBuilder.formSettings.cancelButtonText') || 'Cancel Button Text'}
            </label>
            <input
              type="text"
              value={template.settings.cancelButtonText || ''}
              onChange={(e) => onSettingsUpdate({ cancelButtonText: e.target.value })}
              placeholder="Cancel"
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            />
          </div>

        </div>
      </div>
    </div>
  );
};

// Permissions Settings Tab
const PermissionsSettings: React.FC<{
  template: FormTemplate;
  onPermissionsUpdate: (updates: Partial<FormTemplate['permissions']>) => void;
}> = ({ template, onPermissionsUpdate }) => {
  const { t } = useLanguage();

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">
          {t('formBuilder.formSettings.accessControl') || 'Access Control'}
        </h3>
        
        <div className="space-y-4">
          {/* View Permissions */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t('formBuilder.formSettings.canView') || 'Who can view this form?'}
            </label>
            <textarea
              value={template.permissions.canView.join(', ')}
              onChange={(e) => onPermissionsUpdate({ 
                canView: e.target.value.split(',').map(item => item.trim()).filter(Boolean)
              })}
              placeholder="user@example.com, role:admin, organization:123"
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            />
          </div>

          {/* Edit Permissions */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t('formBuilder.formSettings.canEdit') || 'Who can edit this form?'}
            </label>
            <textarea
              value={template.permissions.canEdit.join(', ')}
              onChange={(e) => onPermissionsUpdate({ 
                canEdit: e.target.value.split(',').map(item => item.trim()).filter(Boolean)
              })}
              placeholder="user@example.com, role:admin"
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            />
          </div>

          {/* Submit Permissions */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t('formBuilder.formSettings.canSubmit') || 'Who can submit this form?'}
            </label>
            <textarea
              value={template.permissions.canSubmit.join(', ')}
              onChange={(e) => onPermissionsUpdate({ 
                canSubmit: e.target.value.split(',').map(item => item.trim()).filter(Boolean)
              })}
              placeholder="user@example.com, role:user, organization:123"
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            />
          </div>

          <div className="text-xs text-gray-500 dark:text-gray-400">
            <p className="mb-1">
              {t('formBuilder.formSettings.permissionsHelp') || 'Permissions format:'}
            </p>
            <ul className="list-disc list-inside space-y-1">
              <li>Email: user@example.com</li>
              <li>Role: role:admin, role:user</li>
              <li>Organization: organization:123</li>
              <li>Everyone: * (use with caution)</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

// Customer Portal Settings Component
const CustomerPortalSettings: React.FC<{
  template: FormTemplate;
  onUpdate: (updates: Partial<FormTemplate>) => void;
}> = ({ template, onUpdate }) => {
  const { customers } = useCustomers();
  const [searchTerm, setSearchTerm] = useState('');

  const customerPortalSettings = template.customerPortalSettings || {
    isAvailableOnPortal: false,
    allowAllCustomers: false,
    allowedCustomers: []
  };

  const handleTogglePortal = (enabled: boolean) => {
    onUpdate({
      customerPortalSettings: {
        ...customerPortalSettings,
        isAvailableOnPortal: enabled
      }
    });
  };

  const handleToggleAllCustomers = (allowAll: boolean) => {
    onUpdate({
      customerPortalSettings: {
        ...customerPortalSettings,
        allowAllCustomers: allowAll,
        allowedCustomers: allowAll ? [] : customerPortalSettings.allowedCustomers
      }
    });
  };

  const handleToggleCustomer = (customerId: string) => {
    const currentAllowed = customerPortalSettings.allowedCustomers || [];
    const isCurrentlyAllowed = currentAllowed.includes(customerId);
    
    onUpdate({
      customerPortalSettings: {
        ...customerPortalSettings,
        allowedCustomers: isCurrentlyAllowed
          ? currentAllowed.filter(id => id !== customerId)
          : [...currentAllowed, customerId]
      }
    });
  };

  const getCustomerName = (customer: any) => {
    if (customer.type === 'organization') {
      return customer.companyName;
    }
    return `${customer.lastName} ${customer.firstName}`;
  };

  const filteredCustomers = customers.filter(customer =>
    getCustomerName(customer).toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="space-y-4">
      {/* Enable on Portal */}
      <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
        <div>
          <label className="text-sm font-medium text-gray-900 dark:text-gray-100">
            Elérhető az ügyfélportálon
          </label>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            Az űrlap megjelenik az ügyfélportálon
          </p>
        </div>
        <label className="relative inline-flex items-center cursor-pointer">
          <input
            type="checkbox"
            checked={customerPortalSettings.isAvailableOnPortal}
            onChange={(e) => handleTogglePortal(e.target.checked)}
            className="sr-only peer"
          />
          <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 dark:peer-focus:ring-primary-800 rounded-full peer dark:bg-gray-600 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-primary-600"></div>
        </label>
      </div>

      {customerPortalSettings.isAvailableOnPortal && (
        <>
          {/* Allow All Customers */}
          <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
            <div>
              <label className="text-sm font-medium text-gray-900 dark:text-gray-100">
                Minden ügyfél számára elérhető
              </label>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Az űrlap minden ügyfél számára látható lesz
              </p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={customerPortalSettings.allowAllCustomers}
                onChange={(e) => handleToggleAllCustomers(e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 dark:peer-focus:ring-primary-800 rounded-full peer dark:bg-gray-600 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-primary-600"></div>
            </label>
          </div>

          {/* Specific Customers */}
          {!customerPortalSettings.allowAllCustomers && (
            <div>
              <label className="block text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">
                Kiválasztott ügyfelek ({customerPortalSettings.allowedCustomers?.length || 0})
              </label>
              
              {/* Search */}
              <input
                type="text"
                placeholder="Keresés ügyfelek között..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-3 py-2 mb-3 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
              />

              {/* Customer List */}
              <div className="max-h-64 overflow-y-auto border border-gray-200 dark:border-gray-600 rounded-md">
                {filteredCustomers.length === 0 ? (
                  <div className="p-4 text-center text-sm text-gray-500 dark:text-gray-400">
                    Nincs találat
                  </div>
                ) : (
                  filteredCustomers.map((customer) => (
                    <label
                      key={customer.id}
                      className="flex items-center p-3 hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer border-b border-gray-100 dark:border-gray-700 last:border-b-0"
                    >
                      <input
                        type="checkbox"
                        checked={customerPortalSettings.allowedCustomers?.includes(customer.id)}
                        onChange={() => handleToggleCustomer(customer.id)}
                        className="w-4 h-4 text-primary-600 bg-gray-100 border-gray-300 rounded focus:ring-primary-500 dark:focus:ring-primary-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                      />
                      <span className="ml-3 text-sm text-gray-900 dark:text-gray-100">
                        {getCustomerName(customer)}
                      </span>
                    </label>
                  ))
                )}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default FormSettings;
